import { ReactNode, useCallback, useMemo, useState } from 'react';
import { Alert, Card, Form, Modal, Tab, Tabs } from 'react-bootstrap';
import cn from 'classnames';
import { MdCheckCircle, MdWarning } from 'react-icons/md';
import {
  BlanketTrafficInstruction,
  BlanketTrafficIsci
} from '@blisspointmedia/bpm-types/dist/LinearBlanketTraffic';
import { PlanRow } from '@blisspointmedia/bpm-types/dist/LinearBuying';
import { Button } from '@Components/Button';
import { Appearance, Intent, Size } from '@Components/designConstants';
import { InteractiveDiv } from '@Components/InteractiveEl';
import { Spinner } from '@Components/Spinner/Spinner';
import { PieSetter } from '@Pages/StreamingCreatives/PieSetter';
import { EditableCard } from '@Pages/StreamingCreatives/Cards';
import { CreativeMap } from '@src/redux/creative';
import { useSetError } from '@src/redux/modals';
import { Mixpanel, MxE } from '@src/utils/mixpanelWrapper';
import { conditionallyPluralizeWithCount } from '@src/utils/text-utils';
import {
  BlanketCombo,
  BlanketRotationRow,
  BlanketValidity,
  DraftDetails,
  MONDAYS_ONLY
} from '../blanket-traffic-constants';
import {
  comboKey,
  comboLabel,
  detailsFromInstruction,
  emptyDetails,
  findInstruction,
  getBlanketCombos,
  getBlanketCreativeOptions,
  getBlanketLengths,
  getBlanketValidity,
  percentKey,
  sameCombo,
  toMonday,
  toSaveParams
} from '../blanket-traffic-utils';
import { deleteBlanketTraffic, saveBlanketTraffic } from '../use-blanket-traffic';

import './BlanketTrafficModal.scss';

const PAGE = 'Linear Buying';

interface BlanketTrafficModalProps {
  company: string;
  /** Week the buy grid is showing, used as the default start week for a new instruction. */
  defaultWeek: string;
  /** Buy rows on screen — the source of both the combination list and each one's spot lengths. */
  planRows: PlanRow[];
  instructions: BlanketTrafficInstruction[];
  isLoading: boolean;
  creativeMap: CreativeMap;
  colorMap: Record<string, string>;
  onSaved: () => void;
  onClose: () => void;
}

export const BlanketTrafficModal = ({
  company,
  defaultWeek,
  planRows,
  instructions,
  isLoading,
  creativeMap,
  colorMap,
  onSaved,
  onClose
}: BlanketTrafficModalProps) => {
  const setError = useSetError();

  const combos = useMemo(() => getBlanketCombos(planRows), [planRows]);

  // The editor opens from the page menu, so it starts on the first combination in the buy.
  const [combo, setCombo] = useState<BlanketCombo | undefined>(() => combos[0]);
  // Edits are held per combination so switching in the rail and back doesn't discard them.
  const [detailsByCombo, setDetailsByCombo] = useState<Record<string, DraftDetails>>({});
  const [percentByCombo, setPercentByCombo] = useState<Record<string, Record<string, number>>>({});
  const [activeLength, setActiveLength] = useState<number>();
  const [saving, setSaving] = useState(false);

  const selected = useMemo(
    () => (combo ? findInstruction(instructions, combo, defaultWeek) : undefined),
    [instructions, combo, defaultWeek]
  );

  const key = combo ? comboKey(combo) : '';
  const details = useMemo(
    () =>
      detailsByCombo[key] ??
      (selected ? detailsFromInstruction(selected) : emptyDetails(defaultWeek)),
    [detailsByCombo, key, selected, defaultWeek]
  );
  const edits = useMemo(() => percentByCombo[key] ?? {}, [percentByCombo, key]);

  const savedRotation = useMemo<BlanketTrafficIsci[]>(() => selected?.iscis ?? [], [selected]);

  /** One tab per length ordered on this combination, or already in its saved rotation. */
  const lengths = useMemo(
    () => (combo ? getBlanketLengths(planRows, combo, savedRotation) : []),
    [planRows, combo, savedRotation]
  );

  const optionsByLength = useMemo(
    () =>
      lengths.map(length => ({
        length,
        options: getBlanketCreativeOptions(creativeMap, colorMap, {
          avail: combo?.avail ?? 'L',
          startWeek: details.startWeek,
          endWeek: details.endWeek || null,
          length,
          // A stored ISCI with no length applies to every length, so it seeds every section --
          // filtering it out would hide it from the editor and then delete it on save, since
          // saving replaces the whole rotation.
          currentRotation: savedRotation.filter(
            isci => isci.length === length || isci.length === null
          )
        })
      })),
    // Which creatives qualify depends only on the scope and the window, so a keystroke in Notes
    // or a percent edit must not rerun the scan over every creative.
    [lengths, creativeMap, colorMap, combo, details.startWeek, details.endWeek, savedRotation]
  );

  // Pending edits layer over the saved percents so an untouched creative keeps its share. Cheap
  // enough to redo on every edit, unlike the scan above.
  const sections = useMemo(
    () =>
      optionsByLength.map(({ length, options }) => {
        const rotation = options.map(option => ({
          ...option,
          percent: edits[percentKey(length, option.isci)] ?? option.percent
        }));
        return { length, rotation, validity: getBlanketValidity(rotation) };
      }),
    [optionsByLength, edits]
  );

  // A length nobody allocated against is simply not covered, so it can't be invalid -- but one
  // that was started has to add up, and at least one has to exist before there's anything to save.
  const startedSections = useMemo(
    () => sections.filter(({ validity }) => validity.hasAllocations),
    [sections]
  );
  const offSections = useMemo(
    () => startedSections.filter(({ validity }) => !validity.isValidTotal),
    [startedSections]
  );

  const updateDetails = useCallback(
    <K extends keyof DraftDetails>(field: K, value: DraftDetails[K]) => {
      setDetailsByCombo(current => ({ ...current, [key]: { ...details, [field]: value } }));
    },
    [details, key]
  );

  const setPercent = useCallback(
    (length: number, isci: string, percent: number) => {
      setPercentByCombo(current => ({
        ...current,
        [key]: { ...(current[key] ?? {}), [percentKey(length, isci)]: percent }
      }));
    },
    [key]
  );

  const onSubmit = useCallback(async () => {
    if (!combo) return;
    setSaving(true);
    try {
      await saveBlanketTraffic({
        id: selected?.id,
        company,
        network: combo.network,
        avail: combo.avail,
        type: combo.type,
        start_week: details.startWeek,
        end_week: details.endWeek || null,
        notes: details.notes || null,
        iscis: toSaveParams(startedSections)
      });
      Mixpanel.track(selected ? MxE.RecordSaved : MxE.RecordCreated, {
        page: PAGE,
        entity: 'blanket traffic instruction'
      });
      onSaved();
      onClose();
    } catch (e) {
      setError({ message: `Couldn't save blanket traffic: ${e.message}`, reportError: e });
    } finally {
      setSaving(false);
    }
  }, [combo, company, details, onClose, onSaved, selected, startedSections, setError]);

  const onDelete = useCallback(async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await deleteBlanketTraffic({ company, id: selected.id });
      Mixpanel.track(MxE.RecordDeleted, { page: PAGE, entity: 'blanket traffic instruction' });
      onSaved();
    } catch (e) {
      setError({ message: `Couldn't delete blanket traffic: ${e.message}`, reportError: e });
    } finally {
      setSaving(false);
    }
  }, [company, onSaved, selected, setError]);

  const hasNonLiveCreative = sections.some(({ rotation }) =>
    rotation.some(({ notLive, percent }) => notLive && percent > 0)
  );
  const datesAreValid =
    Boolean(details.startWeek) && (!details.endWeek || details.endWeek >= details.startWeek);
  const canSave =
    Boolean(combo) &&
    startedSections.length > 0 &&
    offSections.length === 0 &&
    datesAreValid &&
    !saving;

  return (
    <Modal show size='xl' onHide={onClose} className='blanket-traffic-modal'>
      <Modal.Header closeButton>
        <Modal.Title>Blanket Traffic Instructions</Modal.Title>
      </Modal.Header>
      <Modal.Body className='blanket-traffic-body'>
        <ComboList
          combos={combos}
          instructions={instructions}
          defaultWeek={defaultWeek}
          isLoading={isLoading}
          selected={combo}
          onSelect={setCombo}
        />
        <div className='blanket-traffic-editor'>
          {combo ? (
            <>
              <ComboHeader combo={combo} instruction={selected} />
              <DetailsForm details={details} onChange={updateDetails} />
              <LengthTabs
                company={company}
                sections={sections}
                activeLength={activeLength}
                onSelectLength={setActiveLength}
                onChange={setPercent}
              />
            </>
          ) : (
            <div className='blanket-traffic-empty'>
              This buy has no ICON networks for the weeks on screen.
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer className='blanket-traffic-footer'>
        {hasNonLiveCreative && (
          <Alert variant='warning' className='blanket-traffic-alert'>
            <MdWarning /> A creative in this rotation isn&apos;t live for any part of the window.
          </Alert>
        )}
        {!datesAreValid && (
          <Alert variant='danger' className='blanket-traffic-alert'>
            The end week is before the start week.
          </Alert>
        )}
        {offSections.length > 0 && (
          <Alert variant='danger' className='blanket-traffic-alert'>
            {offSections
              .map(({ length, validity }) => `:${length}s is off by ${validity.offBy}%`)
              .join(' · ')}
          </Alert>
        )}
        {selected && (
          <Button
            size={Size.LG}
            design={Intent.ERROR}
            appearance={Appearance.OUTLINED}
            loading={saving}
            onClick={onDelete}
          >
            Delete
          </Button>
        )}
        <Button size={Size.LG} appearance={Appearance.OUTLINED} onClick={onClose}>
          Cancel
        </Button>
        <Button size={Size.LG} disabled={!canSave} loading={saving} onClick={onSubmit}>
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

interface ComboListProps {
  combos: BlanketCombo[];
  instructions: BlanketTrafficInstruction[];
  /** The weeks on screen — a combination counts as set up only for an instruction covering them. */
  defaultWeek: string;
  isLoading: boolean;
  selected?: BlanketCombo;
  onSelect: (combo: BlanketCombo) => void;
}

/**
 * Left rail: every network/avail/buy-type in the buy. There is no "new instruction" affordance —
 * exactly one instruction exists per combination, so picking one either opens the saved
 * instruction or starts the only one that combination can have.
 */
const ComboList = ({
  combos,
  instructions,
  defaultWeek,
  isLoading,
  selected,
  onSelect
}: ComboListProps) => {
  if (isLoading) {
    return (
      <div className='blanket-traffic-list'>
        <Spinner />
      </div>
    );
  }

  return (
    <div className='blanket-traffic-list'>
      {combos.map(combo => {
        const instruction = findInstruction(instructions, combo, defaultWeek);
        return (
          <InteractiveDiv
            key={comboKey(combo)}
            className={cn('blanket-traffic-list-item', {
              active: selected !== undefined && sameCombo(combo, selected)
            })}
            onClick={() => onSelect(combo)}
          >
            <div className='blanket-traffic-list-title'>{combo.network}</div>
            <div className='blanket-traffic-list-detail'>{comboLabel(combo)}</div>
            <div className='blanket-traffic-list-detail'>
              {instruction
                ? `${conditionallyPluralizeWithCount(
                    instruction.iscis.length,
                    'ISCI'
                  )} · rev ${instruction.revision}${instruction.pending ? ' · not sent' : ''}`
                : 'Not set up'}
            </div>
          </InteractiveDiv>
        );
      })}
    </div>
  );
};

interface ComboHeaderProps {
  combo: BlanketCombo;
  instruction?: BlanketTrafficInstruction;
}

/** The scope, shown rather than offered — it's fixed by the row or rail entry you came in on. */
const ComboHeader = ({ combo, instruction }: ComboHeaderProps) => (
  <div className='blanket-traffic-combo-header'>
    <span className='blanket-traffic-combo-network'>{combo.network}</span>
    <span className='blanket-traffic-combo-scope'>{comboLabel(combo)}</span>
    {instruction && (
      <span className='blanket-traffic-combo-scope'>{`Revision ${instruction.revision}`}</span>
    )}
  </div>
);

interface DetailsFormProps {
  details: DraftDetails;
  onChange: <K extends keyof DraftDetails>(field: K, value: DraftDetails[K]) => void;
}

const DetailsForm = ({ details, onChange }: DetailsFormProps) => (
  <div className='blanket-traffic-scope'>
    <Field label='Start Week'>
      <Form.Control
        type='date'
        size='sm'
        {...MONDAYS_ONLY}
        value={details.startWeek}
        onChange={e => onChange('startWeek', toMonday(e.target.value))}
      />
    </Field>
    <Field label='End Week' hint='Leave blank to stay in effect until further notice.'>
      <Form.Control
        type='date'
        size='sm'
        {...MONDAYS_ONLY}
        value={details.endWeek}
        onChange={e => onChange('endWeek', toMonday(e.target.value))}
      />
    </Field>
    <Field label='Notes'>
      <Form.Control
        as='textarea'
        rows={1}
        size='sm'
        value={details.notes}
        onChange={e => onChange('notes', e.target.value)}
      />
    </Field>
  </div>
);

interface FieldProps {
  label: string;
  hint?: string;
  children: ReactNode;
}

const Field = ({ label, hint, children }: FieldProps) => (
  <div className='blanket-traffic-field'>
    <span className='blanket-traffic-field-label'>{label}</span>
    {children}
    {hint && <span className='blanket-traffic-field-hint'>{hint}</span>}
  </div>
);

interface RotationSection {
  length: number;
  rotation: BlanketRotationRow[];
  validity: BlanketValidity;
}

interface LengthTabsProps {
  company: string;
  sections: RotationSection[];
  activeLength?: number;
  onSelectLength: (length: number) => void;
  onChange: (length: number, isci: string, percent: number) => void;
}

/**
 * One tab per spot length ordered on the combination, or already in its saved rotation — each
 * length traffics as its own rotation.
 */
const LengthTabs = ({
  company,
  sections,
  activeLength,
  onSelectLength,
  onChange
}: LengthTabsProps) => {
  if (sections.length === 0) {
    return (
      <Card className='blanket-traffic-rotation'>
        <div className='blanket-traffic-empty'>
          No spots are ordered on this network for the weeks on screen.
        </div>
      </Card>
    );
  }

  // Falls back to the first tab when the selected length isn't on this combination — switching in
  // the rail can land on a network that doesn't carry the length you were just looking at.
  const active =
    sections.find(({ length }) => length === activeLength)?.length ?? sections[0].length;

  return (
    <Tabs
      className='blanket-traffic-tabs'
      activeKey={`${active}`}
      onSelect={selected => onSelectLength(Number(selected))}
    >
      {sections.map(section => (
        <Tab
          key={section.length}
          eventKey={`${section.length}`}
          title={
            <div className='tab-title'>
              {`:${section.length}s`}
              {section.validity.isValid && (
                <div className='valid-indicator'>
                  <MdCheckCircle />
                </div>
              )}
            </div>
          }
        >
          <LengthRotation company={company} section={section} onChange={onChange} />
        </Tab>
      ))}
    </Tabs>
  );
};

interface LengthRotationProps {
  company: string;
  section: RotationSection;
  onChange: (length: number, isci: string, percent: number) => void;
}

const LengthRotation = ({ company, section, onChange }: LengthRotationProps) => {
  const { length, rotation } = section;

  if (rotation.length === 0) {
    return (
      <Card className='blanket-traffic-rotation'>
        <div className='blanket-traffic-empty'>
          {`No creatives are live at :${length}s for this window.`}
        </div>
      </Card>
    );
  }

  return (
    <Card className='blanket-traffic-rotation'>
      <div className='blanket-traffic-rotation-inner'>
        <PieSetter
          pieFlex={0.4}
          data={rotation.map(row => ({ ...row, key: row.isci, value: row.percent }))}
          renderItem={row => (
            <EditableCard
              name={row.isci}
              isci={row.isci}
              // Thumbnails are served per company; without this the URL is built with an empty
              // company segment and every card falls back to "Thumbnail not found".
              company={company}
              creative={row.creative}
              length={length}
              value={row.percent}
              color={row.color}
              notLive={row.notLive}
              onChange={(isci, value) => onChange(length, isci, Math.abs(value || 0))}
            />
          )}
        />
      </div>
    </Card>
  );
};
